/**
 * RequireAuth — affiche un écran "Connecte-toi" inline quand l'utilisateur
 * n'est pas authentifié, sinon rend les enfants normalement.
 *
 * Utilisé sur web pour les sections gated (Looks, ajout d'article).
 * Sur natif, le gate auth est déjà géré au cold start dans App.js.
 *
 * v1.12.117 — refonte web app : browse libre, gate à la demande.
 */

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '@react-navigation/native';
import { useI18n } from '../i18n';

export default function RequireAuth({
  feature = 'this',
  title,
  message,
  ctaLabel,
  children,
}) {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const navigation = useNavigation();
  const { t } = useI18n();

  if (isAuthenticated) return children;

  // v1.12.275 — i18n complet (gates s'affichent sur web pour Profil/Favoris/Dressing/Looks/Voyage)
  // Si title/message passés en prop (legacy : TabNavigator passait des strings FR),
  // on les utilise tels quels ; sinon on prend la clé i18n selon `feature`.
  const heading = title || (
    feature === 'looks'      ? t('require_auth_looks_title')
    : feature === 'wardrobe' ? t('require_auth_wardrobe_title')
    : feature === 'travel'   ? t('require_auth_travel_title')
    : feature === 'favorites'? t('require_auth_favorites_title')
    : feature === 'profile'  ? t('require_auth_profile_title')
    : t('require_auth_default_title')
  );
  const body = message || (
    feature === 'looks'      ? t('require_auth_looks_body')
    : feature === 'wardrobe' ? t('require_auth_wardrobe_body')
    : feature === 'travel'   ? t('require_auth_travel_body')
    : feature === 'favorites'? t('require_auth_favorites_body')
    : feature === 'profile'  ? t('require_auth_profile_body')
    : t('require_auth_default_body')
  );

  return (
    <SafeAreaView style={[s.container, { backgroundColor: theme.bg }]}>
      <View style={s.content}>
        <Text style={s.iconLarge}>✦</Text>
        <Text style={[s.title, { color: theme.text }]}>{heading}</Text>
        <Text style={[s.body, { color: theme.textFaint }]}>{body}</Text>
        <TouchableOpacity
          style={s.cta}
          onPress={() => navigation.navigate('Login')}
          activeOpacity={0.85}
        >
          <Text style={s.ctaText}>{ctaLabel || t('require_auth_cta')}</Text>
        </TouchableOpacity>
        <Text style={[s.hint, { color: theme.textFaint }]}>
          {t('require_auth_hint')}
        </Text>
      </View>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 16,
  },
  iconLarge: {
    fontSize: 32,
    color: '#9B7B5A',
    marginBottom: 4,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 360,
  },
  cta: {
    backgroundColor: '#9B7B5A',
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 100,
    minWidth: 220,
    alignItems: 'center',
    marginTop: 8,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  ctaText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 1,
  },
  hint: {
    fontSize: 11,
    letterSpacing: 0.5,
    marginTop: 4,
  },
});
